/**
 * Layer Preview Component
 *
 * Small map preview for testing layer configuration before saving.
 * Part of Layer Drawer 2.0 - Phase 3 (Admin UI).
 *
 * Features:
 * - Render layer on small Mapbox GL map
 * - Show bounding box and zoom levels
 * - Test layer rendering
 */

import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Layer, CreateLayerInput } from '../../../../types/layer';
import './LayerPreview.scss';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken } from '../../../../services/configService';

interface LayerPreviewProps {
  layerConfig: Partial<CreateLayerInput> | Layer;
  onClose: () => void;
}

export const LayerPreview: React.FC<LayerPreviewProps> = ({ layerConfig, onClose }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize Mapbox GL map
    mapboxgl.accessToken = getMapboxToken();

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-114.0719, 62.4540], // NWT center
        zoom: 5,
        attributionControl: false,
      });

      map.current.on('load', () => {
        setLoading(false);
        addLayerToMap();
      });

      map.current.on('error', (e) => {
        console.error('[LayerPreview] Map error:', e);
        setError('Failed to load map');
      });

      // Cleanup
      return () => {
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      };
    } catch (err: any) {
      console.error('[LayerPreview] Error initializing map:', err);
      setError(err.message || 'Failed to initialize map');
    }
  }, []);

  /**
   * Add layer to map based on configuration
   */
  const addLayerToMap = () => {
    if (!map.current || !layerConfig.type) return;

    try {
      const sourceId = 'preview-source';
      const layerId = 'preview-layer';

      switch (layerConfig.type) {
        case 'wms':
          addWMSLayer(sourceId, layerId);
          break;

        case 'tms':
          addTMSLayer(sourceId, layerId);
          break;

        case 'wfs':
        case 'geojson':
          addVectorLayer(sourceId, layerId);
          break;

        default:
          setError(`Preview not supported for layer type: ${layerConfig.type}`);
      }
    } catch (err: any) {
      console.error('[LayerPreview] Error adding layer:', err);
      setError(err.message || 'Failed to add layer to map');
    }
  };

  /**
   * Add WMS layer
   */
  const addWMSLayer = (sourceId: string, layerId: string) => {
    if (!map.current || !layerConfig.config?.layers || !layerConfig.source_url) {
      setError('WMS layer requires source URL and layer name');
      return;
    }

    const config = layerConfig.config;

    map.current.addSource(sourceId, {
      type: 'raster',
      tiles: [
        `${layerConfig.source_url}?` +
          `service=WMS&` +
          `version=${config.version || '1.3.0'}&` +
          `request=GetMap&` +
          `layers=${config.layers}&` +
          `styles=${config.styles || ''}&` +
          `format=${config.format || 'image/png'}&` +
          `transparent=${config.transparent ?? true}&` +
          `crs=${config.crs || 'EPSG:3857'}&` +
          `width=256&` +
          `height=256&` +
          `bbox={bbox-epsg-3857}`,
      ],
      tileSize: 256,
    });

    map.current.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      paint: {
        'raster-opacity': layerConfig.default_opacity ?? 1.0,
      },
      minzoom: layerConfig.zoom_min ?? 0,
      maxzoom: layerConfig.zoom_max ?? 22,
    });
  };

  /**
   * Add TMS layer
   */
  const addTMSLayer = (sourceId: string, layerId: string) => {
    if (!map.current || !layerConfig.config?.tileUrl) {
      setError('TMS layer requires tile URL');
      return;
    }

    const config = layerConfig.config;

    map.current.addSource(sourceId, {
      type: 'raster',
      tiles: [config.tileUrl],
      tileSize: config.tileSize || 256,
      scheme: config.scheme || 'xyz',
      attribution: config.attribution || '',
    });

    map.current.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      paint: {
        'raster-opacity': layerConfig.default_opacity ?? 1.0,
      },
      minzoom: layerConfig.zoom_min ?? 0,
      maxzoom: layerConfig.zoom_max ?? 22,
    });
  };

  /**
   * Add vector layer (WFS or GeoJSON)
   */
  const addVectorLayer = (sourceId: string, layerId: string) => {
    if (!map.current) return;

    const dataUrl =
      layerConfig.type === 'wfs'
        ? buildWFSUrl()
        : layerConfig.config?.dataUrl;

    if (!dataUrl) {
      setError('Vector layer requires data URL');
      return;
    }

    map.current.addSource(sourceId, {
      type: 'geojson',
      data: dataUrl,
    });

    // Get custom style from config, or use defaults
    const style = layerConfig.config?.style || {};
    const layerType = style.type || 'circle';

    // Default paint styles by type
    const defaultPaint: Record<string, any> = {
      circle: {
        'circle-radius': 6,
        'circle-color': '#007cbf',
        'circle-opacity': layerConfig.default_opacity ?? 1.0,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      },
      line: {
        'line-color': '#007cbf',
        'line-width': 2,
        'line-opacity': layerConfig.default_opacity ?? 1.0
      },
      fill: {
        'fill-color': '#007cbf',
        'fill-opacity': (layerConfig.default_opacity ?? 1.0) * 0.5,
        'fill-outline-color': '#004d7a'
      }
    };

    // Merge default paint with custom paint from config
    const paint = {
      ...defaultPaint[layerType],
      ...style.paint
    };

    map.current.addLayer({
      id: layerId,
      type: layerType as any,
      source: sourceId,
      paint: paint,
      minzoom: layerConfig.zoom_min ?? 0,
      maxzoom: layerConfig.zoom_max ?? 22,
    });
  };

  /**
   * Build WFS GetFeature URL
   */
  const buildWFSUrl = (): string | null => {
    if (!layerConfig.source_url || !layerConfig.config?.typeName) {
      return null;
    }

    const config = layerConfig.config;
    const params = new URLSearchParams({
      service: 'WFS',
      version: config.version || '2.0.0',
      request: 'GetFeature',
      typeName: config.typeName,
      outputFormat: config.outputFormat || 'application/json',
      srsName: 'EPSG:4326',
    });

    if (config.maxFeatures) {
      params.append('maxFeatures', String(config.maxFeatures));
    }

    if (config.cqlFilter) {
      params.append('cql_filter', config.cqlFilter);
    }

    return `${layerConfig.source_url}?${params.toString()}`;
  };

  return (
    <div className="layer-preview-overlay">
      <div className="layer-preview-modal">
        <button onClick={onClose} className="modal-close-btn">
          ×
        </button>

        <h3 className="preview-title">Layer Preview</h3>

        <div className="preview-info">
          <div className="info-item">
            <strong>Layer:</strong> {layerConfig.name || 'Untitled'}
          </div>
          <div className="info-item">
            <strong>Type:</strong> {layerConfig.type?.toUpperCase()}
          </div>
          <div className="info-item">
            <strong>Zoom Range:</strong> {layerConfig.zoom_min ?? 0} -{' '}
            {layerConfig.zoom_max ?? 22}
          </div>
        </div>

        {loading && (
          <div className="preview-loading">
            <p>Loading map...</p>
          </div>
        )}

        {error && (
          <div className="preview-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div
          ref={mapContainer}
          className="preview-map-container"
          style={{ width: '100%', height: '400px' }}
        />

        <div className="preview-actions">
          <button onClick={onClose} className="btn btn-primary">
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};
